type StatusMessagesProps = {
  submitStatus: 'idle' | 'success' | 'error';
  requestId: string;
  errorMessage: string;
  onDismiss: () => void;
};

export const StatusMessages = ({
  submitStatus,
  requestId,
  errorMessage,
  onDismiss,
}: StatusMessagesProps) => {
  if (submitStatus === 'success') {
    return (
      <div className="mb-6 p-6 bg-[#026857]/10 border-2 border-[#026857] rounded-lg">
        <div className="flex items-start gap-3 mb-4">
          <svg
            className="w-6 h-6 text-[#026857] flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <h4 className="text-xl font-recoleta font-semibold text-[#026857] mb-2">
              Thank you for your submission!
            </h4>
            <p className="text-sm text-[#4B5563] font-termina mb-3">
              Your request ID is <span className="font-mono font-semibold">{requestId}</span>.
            </p>
            <p className="text-base text-[#111827] font-termina mb-4">
              A member of bQuest's team will review your request and contact you within 3 business
              days to help connect you with the appropriate care provider.
            </p>

            <div className="bg-white p-4 rounded-lg border border-[#026857]/20">
              <h5 className="font-termina font-semibold text-[#026857] mb-3">
                The bQuest Care Concierge Process: What you can expect from us
              </h5>

              <div className="space-y-4">
                <div>
                  <h6 className="font-termina font-semibold text-[#111827] mb-1">
                    Initial Update / Shortlist Delivery: Within 3 business days (72 hours)
                  </h6>
                  <p className="text-sm text-[#4B5563] font-termina mb-2">
                    You will receive:
                  </p>
                  <ul className="list-disc list-inside text-sm text-[#4B5563] font-termina space-y-1 ml-2">
                    <li>
                      A shortlist of qualified providers identified through extended network search
                    </li>
                    <li>
                      Or status update if more time is needed, specifying when to expect final
                      matches
                    </li>
                  </ul>
                  <p className="text-sm text-[#4B5563] font-termina mt-2 italic">
                    For higher-complexity or rare specialties, we will communicate proactively about
                    how we're engaging our national network of providers.
                  </p>
                </div>

                <div>
                  <h6 className="font-termina font-semibold text-[#111827] mb-1">
                    Final Sourcing & Introduction: Within 5-7 business days
                  </h6>
                  <p className="text-sm text-[#4B5563] font-termina mb-2">
                    You will receive:
                  </p>
                  <ul className="list-disc list-inside text-sm text-[#4B5563] font-termina space-y-1 ml-2">
                    <li>1-3 vetted provider options, or</li>
                    <li>
                      Confirmation that no qualified providers currently meet your vetting
                      standards, along with a plan for follow-up
                    </li>
                  </ul>
                  <p className="text-sm text-[#4B5563] font-termina mt-2 italic">
                    For ultra-specific or rural requests, timeline may extend to 10 business days,
                    but this will be rare and clearly communicated upfront.
                  </p>
                </div>
              </div>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="text-[#6B7280] hover:text-[#111827] transition-colors flex-shrink-0"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  if (submitStatus === 'error') {
    return (
      <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
        <div className="flex items-start gap-3">
          <svg
            className="w-6 h-6 text-red-600 flex-shrink-0 mt-0.5"
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2}
              d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
            />
          </svg>
          <div className="flex-1">
            <h4 className="font-termina font-semibold text-red-800 mb-1">
              Error submitting request
            </h4>
            <p className="text-sm text-red-700 font-termina">{errorMessage}</p>
          </div>
          <button
            onClick={onDismiss}
            className="text-red-600 hover:text-red-800 transition-colors"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>
      </div>
    );
  }

  return null;
};
